import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../../utils/baseQuery";
import {
  ApiResponse,
  ControlSettingsDto,
  DecisionRequest,
  GenerateResult,
  Recommendation,
  RecommendationListResponse,
  RecommendationsQueryParams,
} from "../../types/control";

export const controlApi = createApi({
  reducerPath: "controlApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["ControlRecommendations", "ControlSettings"],
  endpoints: (builder) => ({
    // GET /api/control/recommendations — lista paginada con filtros
    getRecommendations: builder.query<
      ApiResponse<RecommendationListResponse>,
      RecommendationsQueryParams | void
    >({
      query: (params) => {
        const search = new URLSearchParams();
        if (params) {
          const {
            page,
            pageSize,
            status,
            direction,
            applyStatus,
            companyId,
            startDate,
            endDate,
            search: q,
          } = params;
          if (page != null) search.append("page", String(page));
          if (pageSize != null) search.append("pageSize", String(pageSize));
          if (status) search.append("status", status);
          if (direction) search.append("direction", direction);
          if (applyStatus) search.append("applyStatus", applyStatus);
          if (companyId) search.append("companyId", companyId);
          if (startDate) search.append("startDate", startDate);
          if (endDate) search.append("endDate", endDate);
          if (q) search.append("search", q);
        }
        const qs = search.toString();
        return `/control/recommendations${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["ControlRecommendations"],
    }),

    // GET /api/control/recommendations/{id} — detalle (incluye metricsSnapshot)
    getRecommendationById: builder.query<ApiResponse<Recommendation>, string>({
      query: (id) => `/control/recommendations/${id}`,
      providesTags: (_result, _error, id) => [
        { type: "ControlRecommendations", id },
      ],
    }),

    // POST /api/control/recommendations/{id}/accept — aplica el cambio en DV360
    acceptRecommendation: builder.mutation<
      ApiResponse<Recommendation>,
      { id: string; note?: string }
    >({
      query: ({ id, note }) => ({
        url: `/control/recommendations/${id}/accept`,
        method: "POST",
        body: { note } as DecisionRequest,
      }),
      invalidatesTags: ["ControlRecommendations"],
    }),

    // POST /api/control/recommendations/{id}/reject
    rejectRecommendation: builder.mutation<
      ApiResponse<Recommendation>,
      { id: string; note?: string }
    >({
      query: ({ id, note }) => ({
        url: `/control/recommendations/${id}/reject`,
        method: "POST",
        body: { note } as DecisionRequest,
      }),
      invalidatesTags: ["ControlRecommendations"],
    }),

    // POST /api/control/recommendations/{id}/retry-apply — reintenta las FAILED
    retryApply: builder.mutation<ApiResponse<Recommendation>, string>({
      query: (id) => ({
        url: `/control/recommendations/${id}/retry-apply`,
        method: "POST",
      }),
      invalidatesTags: ["ControlRecommendations"],
    }),

    // POST /api/control/generate — regenera avisos
    generateRecommendations: builder.mutation<
      ApiResponse<GenerateResult>,
      void
    >({
      query: () => ({ url: "/control/generate", method: "POST" }),
      invalidatesTags: ["ControlRecommendations"],
    }),

    // GET /api/control/settings
    getControlSettings: builder.query<ApiResponse<ControlSettingsDto>, void>({
      query: () => "/control/settings",
      providesTags: ["ControlSettings"],
    }),

    // PUT /api/control/settings (solo ADMIN/STRATEGY) — body parcial
    updateControlSettings: builder.mutation<
      ApiResponse<ControlSettingsDto>,
      Partial<ControlSettingsDto>
    >({
      query: (body) => ({
        url: "/control/settings",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["ControlSettings"],
    }),
  }),
});

export const {
  useGetRecommendationsQuery,
  useGetRecommendationByIdQuery,
  useLazyGetRecommendationByIdQuery,
  useAcceptRecommendationMutation,
  useRejectRecommendationMutation,
  useRetryApplyMutation,
  useGenerateRecommendationsMutation,
  useGetControlSettingsQuery,
  useUpdateControlSettingsMutation,
} = controlApi;
